import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import { stringToDate } from 'modules/core/lib/dates'
import { convertHoursToTimeFormat } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import DateRangePicker from 'modules/timeoff/components/DateRangePicker'
import { useMinimumLeaveDate } from 'modules/timeoff/components/LeaveContent/OtherLeavesForm/DayTypeForm/DayTypes/useMinimumLeaveDate'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import Allowance from 'modules/timeoff/store/models/Allowance'

import AbsenceSummaryContent from './AbsenceSummary'

interface Props {
  form: LeaveForm
  holidays: DaysTree
  blockedPeriods: DaysTree
  existingLeaves: DaysTree
  readOnly?: boolean
  bulkAdd?: boolean
  timeTaken?: string
  labourUnits?: LabourUnitsResult
  isReviewOrEditMode?: boolean
}

const Days = ({
  readOnly,
  form,
  holidays,
  blockedPeriods,
  existingLeaves,
  bulkAdd,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
}: Props) => {
  const endDateOptional = form.endDateOptional
  const { startsOn } = useContractsVersion()
  const minDate = useMinimumLeaveDate(startsOn)

  const finishOnField = form.get('finish_on')
  const startOnField = form.get('start_on')

  const setStartOnValue = (value: string) => {
    startOnField.set(value)

    if (form.startOn.isAfter(form.finishOn)) setFinishOnValue(value)
  }

  const setFinishOnValue = (value: string) => {
    finishOnField.set(value)

    if (form.finishOn.isBefore(form.startOn)) setStartOnValue(value)
  }

  const calendarEventStartOn = startOnField.value
    ? stringToDate(startOnField.value, 'ui').hour(12).toDate()
    : new Date()

  const calendarEventFinishOn =
    stringToDate(finishOnField.value, 'ui').hour(12).toDate() || calendarEventStartOn
  const calendarEventFinishOnNatural = labourUnits?.finishOnNatural
    ? moment(labourUnits.finishOnNatural, 'YYYY-MM-DD', true).hour(12).toDate()
    : calendarEventStartOn

  const calculateCalendarEventFinishOn = (): Date => {
    if (!finishOnField.value && calendarEventStartOn) {
      return calendarEventStartOn
    }

    return (labourUnits?.isNatural || labourUnits?.countSmRestDays) && labourUnits?.finishOnNatural
      ? calendarEventFinishOnNatural
      : calendarEventFinishOn
  }

  const labourUnitValue = labourUnits?.value

  const isLabourUnitsHours = labourUnits?.label === Allowance.ALLOWANCE_TYPES.hours

  const daysText = i18n.tp('time_off.days_with_years', {
    count: labourUnits?.value || 0,
  })

  const hoursText = convertHoursToTimeFormat(labourUnitValue || 0, false)

  const timeToRequest = isLabourUnitsHours ? hoursText : daysText

  const isStartDateEqualFinishDate =
    calendarEventStartOn.getTime() === calculateCalendarEventFinishOn().getTime()

  const hideAbsenceSummary =
    (!finishOnField.value && !labourUnits?.isNatural) || labourUnits?.label === ''

  return (
    <>
      {isReviewOrEditMode ? (
        <FormRow>
          <DateRangePicker
            startDate={form.get('start_on')}
            endDate={form.get('finish_on')}
            readonly={!!readOnly}
            holidays={holidays}
            blockedPeriods={blockedPeriods}
            existingLeaves={existingLeaves}
            minDate={minDate.current}
            withNull={endDateOptional}
            bulkAdd={bulkAdd}
          />
        </FormRow>
      ) : (
        <>
          <FormRow>
            <DateRangePicker
              startDate={form.get('start_on')}
              endDate={form.get('finish_on')}
              readonly={!!readOnly}
              holidays={holidays}
              blockedPeriods={blockedPeriods}
              existingLeaves={existingLeaves}
              minDate={minDate.current}
              withNull={endDateOptional}
              bulkAdd={bulkAdd}
            />
          </FormRow>
          {!hideAbsenceSummary && (
            <FormRow>
              <AbsenceSummaryContent
                title={form.type?.name || ''}
                description={timeTaken || timeToRequest}
                fromDate={isStartDateEqualFinishDate ? undefined : calendarEventStartOn}
                toDate={calculateCalendarEventFinishOn()}
              />
            </FormRow>
          )}
        </>
      )}
    </>
  )
}

export default observer(Days)
